// -----------------------------------------------------------------------
// <copyright file="input-new-pswd.component.ts" company="AntemetA">
//     Copyright (c) AntemetA. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------
import { Component, Optional, Inject, Injector, OnInit, ElementRef, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, NG_ASYNC_VALIDATORS, } from '@angular/forms';

import { Subject, Subscription } from 'rxjs';
import { map, debounceTime, concatMap } from 'rxjs/operators';
import type { ZXCVBNScore } from 'zxcvbn';

import { InputBase } from '@ui/components/form/base/input-base';
import { ZxcvbnModule } from '@common/types/types';

@Component({
    selector: 'aa-input-new-pswd',
    templateUrl: './input-new-pswd.component.html',
    styleUrls: [
        '../input.component.scss',
        './input-new-pswd.component.scss'
    ],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: InputNewPswdComponent,
            multi: true,
        }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class InputNewPswdComponent extends InputBase<string> implements OnInit, OnDestroy
{
    private static identifier: number = 0;
    public typeInput: string = 'password';
    public extra: string = 'fa-eye-slash';
    private showHide: boolean = false;
    private timer: NodeJS.Timeout;
    public strengthMsg: string = '';
    public keyUp: Subject<any> = new Subject<any>();
    private subscription: Subscription;
    private indicators: NodeListOf<Element>;
    // ! FIXME Traductions manquantes
    private strength: string[] = [
        'Très mauvais',
        'Mauvais',
        'Faible',
        'Bon',
        'Fort'
    ];
    private strengthClass: string[] = [
        'worst',
        'bad',
        'weak',
        'good',
        'strong'
    ];

    constructor (
        private elementRef: ElementRef,
        @Optional() @Inject(NG_VALIDATORS) validators: Array<any>,
        @Optional() @Inject(NG_ASYNC_VALIDATORS) asyncValidators: Array<any>,
        injector: Injector
    )
    {
        super(injector);

        this.fieldId = `input-new-pswd-${InputNewPswdComponent.identifier++}-id`;
    }

    ngOnInit ()
    {
        this.indicators = this.elementRef.nativeElement.querySelector('.password-strength').querySelectorAll('.indicator');

        this.subscription = this.keyUp
            .pipe(
                map(event => event.target.value),
                debounceTime(250),
                // distinctUntilChanged(),
                // flatMap(search => of(search).pipe(delay(100))),
                concatMap((inputValue: string) => import('zxcvbn')
                    .then((zxcvbnModule: ZxcvbnModule) =>
                    {
                        this.passwordStrength(zxcvbnModule.default(inputValue).score);
                    })
                )
            )
            .subscribe();
    }

    ngOnDestroy (): void
    {
        clearTimeout(this.timer);
        this.subscription.unsubscribe();
    }

    viewPswd ()
    {
        this.showHide = !this.showHide;
        this.typeInput = this.showHide ? 'text' : 'password';
        if (this.showHide)
        {
            this.typeInput = 'text';
            this.extra = 'fa-eye';
            this.initiateTimer();
        }
        else
        {
            this.typeInput = 'password';
            this.extra = 'fa-eye-slash';
        }
    }

    private initiateTimer ()
    {
        if (this.timer)
        {
            clearTimeout(this.timer);
        }

        this.timer = setTimeout(() =>
        {
            if (this.showHide)
            {
                this.viewPswd();
            }
        }, 2500);
    }

    private passwordStrength (score: ZXCVBNScore): void
    {
        this.indicators.forEach((item, idx) =>
        {
            item.classList.remove(...this.strengthClass);
            if (idx <= score)
            {
                item.classList.add(this.strengthClass[score]);
            }
        });
        this.strengthMsg = this.strength[score];
    }
}
